// A space on a phone, after SPhoneSpace: back to Spaces and + New activity; the client over the space's name, the
// assistant's week, the Context row (opens the space's context in a sheet), the Knowledge row (its folders and the
// Team-wide ones, in a sheet), the tracks as chips (one picked: Open the
// track and its summary), Open, Mine or Done, then the activities as rows.
import { AddButton } from '../ui/AddButton';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { can, type SessionUser, type SpaceDetail } from '@dpw/shared';
import { useSpaceActivities } from '../lib/queries';
import { HEALTH } from '../ui/bits';
import { ContextPanel } from '../ui/ContextPanel';
import { NewActivity } from '../ui/NewActivity';
import { NewTrack } from '../ui/NewTrack';
import { MembersSheet, managesMembers } from '../ui/Members';
import { SkelList } from '../ui/motion';
import { BriefCard, P, PSheet, Row, Seg, Title, TopBar } from './kit';
import { ActivityRow, whenLine } from './rows';
import { useMarkSeen, useNewSince } from '../lib/seen';
import { NewDot } from '../ui/NewDot';
import { SpaceKnowledgeSheet, knowledgeSub } from './Folders';
import { useFolders } from '../kb/hooks';

type Show = 'open' | 'mine' | 'done';

export function PhoneSpace({ s, me }: { s: SpaceDetail; me: SessionUser }) {
  const [track, setTrack] = useState('all');
  const dots = useNewSince();
  const markSeen = useMarkSeen();
  const [show, setShow] = useState<Show>('open');
  const [sheet, setSheet] = useState<'none' | 'context' | 'new' | 'knowledge' | 'track' | 'people'>('none');
  const folders = useFolders();
  const acts = useSpaceActivities(s.id, { trackId: track === 'all' ? undefined : track, mine: show === 'mine', includeDone: show === 'done' });
  const list = (acts.data ?? []).filter(a => (show === 'done' ? a.status === 'done' : a.status !== 'done'));
  const total = s.tracks.reduce((n, t) => n + t.total, 0);
  const open = s.tracks.reduce((n, t) => n + t.open, 0);
  const sel = s.tracks.find(t => t.id === track);
  const next = (acts.data ?? []).filter(a => a.status !== 'done' && (a.startsAt || a.dueDate)).sort((x, y) => (x.startsAt ?? x.dueDate!).localeCompare(y.startsAt ?? y.dueDate!))[0];
  const writer = can(me.role, 'activity.write');
  return (
    <div className="ph-page">
      <TopBar back={{ to: '/spaces', label: 'Spaces' }}>
        {writer && <button type="button" className="ph-nb" aria-label="New activity" onClick={() => setSheet('new')}><P.plus /></button>}
      </TopBar>
      <Title eyebrow={s.clientName ?? 'Internal'} title={s.name} sub={`${total} activities · ${open} open · ${s.members.length} people`} />
      <BriefCard kind="space" id={s.id} label="This week" />
      <div className="gap16" />
      <div className="ph-grp">
        <Row onClick={() => setSheet('context')} icon={<P.book />} title="Context" sub="What the assistant knows about this space" />
        <Row onClick={() => setSheet('knowledge')} icon={<P.folder />} title="Knowledge" sub={knowledgeSub(folders.data ?? [], s.id)} />
        {managesMembers(me, s) && <Row onClick={() => setSheet('people')} icon={<P.people />} title="People" sub={`${s.members.length} in this space · add or remove`} />}
      </div>
      <div className="gap16" />
      <div className="ph-chips" role="tablist" aria-label="Tracks">
        <button type="button" role="tab" aria-selected={track === 'all'} className={`ph-chip${track === 'all' ? ' on' : ''}`} onClick={() => setTrack('all')}>All tracks</button>
        {s.tracks.map(t => (
          <button key={t.id} type="button" role="tab" aria-selected={track === t.id} className={`ph-chip${track === t.id ? ' on' : ''}`} onClick={() => { setTrack(t.id); if (dots.trackIsNew(t.id)) markSeen('track', t.id); }}>
            <i style={{ background: t.color }} />{t.name}{dots.track(t.id) && <NewDot />}
          </button>
        ))}
        {can(me.role, 'track.write') && <button type="button" className="ph-chip add" onClick={() => setSheet('track')} aria-label="New track"><P.plus />Track</button>}
      </div>
      {!s.tracks.length && <div className="ph-empty3" role="status"><b>No tracks yet</b>
        <span>Every activity sits on a track (a workstream). {can(me.role, 'track.write') ? 'Add the first one.' : 'The space lead or the director adds them.'}</span>
        {can(me.role, 'track.write') && <AddButton className="wide" onClick={() => setSheet('track')}>New track</AddButton>}</div>}
      {sel && <>
        <div className="gap16" />
        <Link className="ph-btf sec" to={`/tracks/${sel.id}`}>Open {sel.name}<P.chev /></Link>
        <div className="gap8" />
        <div className="ph-tsum" aria-label={`${sel.name} summary`}>
          <div><span>Lead</span><b>{sel.lead?.name.split(' ')[0] ?? 'No lead'}</b></div>
          <div><span>Health</span><b className={HEALTH[sel.health][1]}>{HEALTH[sel.health][0]}</b></div>
          <div><span>Open</span><b>{sel.open} of {sel.total}</b></div>
          <div><span>Next</span><b>{next ? `${whenLine(next).replace(/ · \d\d:\d\d$/, '')}` : 'Nothing dated'}</b></div>
        </div>
      </>}
      <div className="gap16" />
      <Seg label="Show" value={show} onChange={setShow} options={[['open', 'Open'], ['mine', 'Mine'], ['done', 'Done']] as const} />
      <div className="gap16" />
      {acts.isLoading ? <SkelList rows={5} variant="conv" /> : (
        <div className="ph-grp" role="group" aria-label="Activities">
          {list.map(a => <ActivityRow key={a.id} a={a} sub={sel ? a.owners[0]?.name.split(' ')[0] ?? 'No owner' : a.trackName} />)}
          {!list.length && <div className="ph-rowx"><span className="ph-rt"><div className="s">Nothing here.</div></span></div>}
        </div>
      )}
      <AnimatePresence>
        {sheet === 'context' && (
          <PSheet key="ctx" label={`${s.name} context`} onClose={() => setSheet('none')}>
            <div className="ph-ctx"><ContextPanel kind="space" id={s.id} spaceOf={s.id} /></div>
          </PSheet>
        )}
        {sheet === 'knowledge' && <SpaceKnowledgeSheet key="kn" s={s} onClose={() => setSheet('none')} />}
      </AnimatePresence>
      {sheet === 'new' && <NewActivity space={s} me={me} trackId={track === 'all' ? undefined : track} side="bottom" onClose={() => setSheet('none')} onNewTrack={can(me.role, 'track.write') ? () => setSheet('track') : undefined} />}
      <AnimatePresence>{sheet === 'people' && <MembersSheet key="people" spaceId={s.id} onClose={() => setSheet('none')} />}</AnimatePresence>
      <AnimatePresence>{sheet === 'track' && <NewTrack key="track" spaceId={s.id} members={s.members} onClose={() => setSheet('none')} />}</AnimatePresence>
    </div>
  );
}
